import { clear, h } from "../ui/dom.js";
import {
  LIMITS,
  TIERS,
  addExplore,
  addRoom,
  canAddRoom,
  canSetTier,
  clearPlan,
  emptyPlan,
  exportPlan,
  loadPlan,
  parsePlan,
  removeExplore,
  removeRoom,
  roomLimit,
  savePlan,
  setTier,
  updateRoom,
  validate,
  type FloorPlan,
  type KeyValueStore,
  type Room,
  type Tier
} from "./model.js";

export interface RetailerConfigDeps {
  storage?: KeyValueStore;
  announce?: (text: string) => void;
  close?: () => void;
}

let uid = 0;
const rooms = (n: number) => `${n} room${n === 1 ? "" : "s"}`;

/** The retailer floor planner shown on the Atelier design table. All text goes through textContent. */
export function retailerConfigContent(d: RetailerConfigDeps = {}): HTMLElement {
  let plan: FloorPlan = loadPlan(d.storage);
  const p = `rc${uid++}`;
  const root = h("section", { class: "rc", "data-testid": "retailer-config", "aria-labelledby": `${p}-title` });
  const status = h("p", { class: "rc-status", role: "status", "aria-live": "polite", "data-testid": "rc-status" });
  const body = h("div", { class: "rc-body" });
  const say = (t: string) => {
    status.textContent = t;
    d.announce?.(t);
  };
  const commit = (next: FloorPlan, rerender = true, focus?: string) => {
    plan = next;
    savePlan(plan, d.storage);
    if (rerender) render(focus);
  };

  const field = (id: string, label: string, control: HTMLInputElement | HTMLTextAreaElement, hint?: string) =>
    h("div", { class: "rc-field" }, h("label", { for: id }, label), control, hint ? h("span", { class: "rc-hint" }, hint) : null);

  function roomCard(r: Room, i: number): HTMLElement {
    const id = `${p}-${r.id}`;
    const set = (patch: (x: Room) => Room) => commit(updateRoom(plan, r.id, patch), false);
    const name = h("input", { id: `${id}-name`, type: "text", maxlength: LIMITS.name, "data-field": "name", autocomplete: "off" });
    name.value = r.name;
    name.addEventListener("input", () => {
      set((x) => ({ ...x, name: name.value }));
      heading.textContent = name.value.trim() || `Room ${i + 1}`;
    });
    const desc = h("textarea", { id: `${id}-desc`, rows: 3, maxlength: LIMITS.description, "data-field": "description", placeholder: "Materials, lighting, colours, what you see as you walk in" });
    desc.value = r.description;
    desc.addEventListener("input", () => set((x) => ({ ...x, description: desc.value })));
    const aName = h("input", { id: `${id}-aname`, type: "text", maxlength: LIMITS.assistantName, "data-field": "assistantName", autocomplete: "off" });
    aName.value = r.assistant.name;
    aName.addEventListener("input", () => set((x) => ({ ...x, assistant: { ...x.assistant, name: aName.value } })));
    const aRole = h("textarea", { id: `${id}-arole`, rows: 2, maxlength: LIMITS.assistantRole, "data-field": "assistantRole", placeholder: "Who they are and how they help guests" });
    aRole.value = r.assistant.role;
    aRole.addEventListener("input", () => set((x) => ({ ...x, assistant: { ...x.assistant, role: aRole.value } })));

    const list = h("ul", { class: "rc-explore", "aria-label": `Things to explore in room ${i + 1}` });
    r.explore.forEach((item, j) =>
      list.append(
        h("li", {}, h("span", {}, item),
          h("button", { type: "button", class: "btn rc-x", "aria-label": `Remove "${item}"`, "data-testid": "rc-explore-remove", onclick: () => {
            commit(updateRoom(plan, r.id, (x) => removeExplore(x, j)), true, `${id}-explore`);
            say(`Removed "${item}".`);
          } }, "Remove"))
      )
    );
    const exInput = h("input", { id: `${id}-explore`, type: "text", maxlength: LIMITS.explore, "data-field": "explore", placeholder: "e.g. the vault of archive pieces", autocomplete: "off" });
    const addEx = () => {
      const v = exInput.value.trim();
      if (!v) return say("Type something to explore first.");
      if (r.explore.length >= LIMITS.exploreItems) return say(`A room can list up to ${LIMITS.exploreItems} things to explore.`);
      commit(updateRoom(plan, r.id, (x) => addExplore(x, v)), true, `${id}-explore`);
      say(`Added "${v}".`);
    };
    exInput.addEventListener("keydown", (e) => {
      if ((e as KeyboardEvent).key === "Enter") {
        e.preventDefault();
        addEx();
      }
    });

    const heading = h("h3", { class: "rc-room-title", id: `${id}-h` }, r.name.trim() || `Room ${i + 1}`);
    return h("fieldset", { class: "rc-room", "data-testid": "rc-room", "data-room": r.id, "aria-labelledby": `${id}-h` },
      h("div", { class: "rc-room-head" },
        h("span", { class: "rc-room-no", "aria-hidden": "true" }, String(i + 1)),
        heading,
        h("button", { type: "button", class: "btn rc-remove", "data-testid": "rc-remove-room", "aria-label": `Remove room ${i + 1}`, onclick: () => {
          const label = r.name.trim() || `Room ${i + 1}`;
          commit(removeRoom(plan, r.id), true, `${p}-add`);
          say(`Removed ${label}. ${plan.rooms.length} of ${roomLimit(plan.tier)} rooms used.`);
        } }, "Remove room")),
      field(`${id}-name`, "Room name", name),
      field(`${id}-desc`, "What the room looks like", desc),
      h("div", { class: "rc-pair" },
        field(`${id}-aname`, "Assistant name", aName),
        field(`${id}-arole`, "Assistant persona and role", aRole)),
      h("div", { class: "rc-field" },
        h("span", { class: "rc-label" }, "Things to explore"),
        list,
        h("label", { for: `${id}-explore`, class: "sr-only" }, "Add a thing to explore"),
        h("div", { class: "rc-add-explore" }, exInput,
          h("button", { type: "button", class: "btn", "data-testid": "rc-explore-add", onclick: addEx }, "Add")))
    );
  }

  function render(focusId?: string): void {
    clear(body);
    const limit = roomLimit(plan.tier);
    const full = !canAddRoom(plan);

    const retailer = h("input", { id: `${p}-retailer`, type: "text", maxlength: LIMITS.retailer, "data-field": "retailer", autocomplete: "organization" });
    retailer.value = plan.retailer;
    retailer.addEventListener("input", () => commit({ ...plan, retailer: retailer.value }, false));

    const tiers = h("div", { class: "rc-tiers", role: "radiogroup", "aria-labelledby": `${p}-tier-l` });
    for (const t of TIERS) {
      const ok = canSetTier(plan, t);
      const on = plan.tier === t;
      tiers.append(
        h("button", {
          type: "button", role: "radio", class: `rc-tier${on ? " is-selected" : ""}`, "aria-checked": String(on), "data-tier": t,
          "aria-disabled": ok ? undefined : "true", title: ok ? undefined : `Remove rooms to fit ${rooms(roomLimit(t))}`,
          onclick: () => {
            if (on) return;
            if (!ok) return say(`Tier ${t} allows ${rooms(roomLimit(t))}. Remove ${plan.rooms.length - roomLimit(t)} first.`);
            commit(setTier(plan, t as Tier), true, undefined);
            say(`Tier ${t} selected: up to ${rooms(roomLimit(t))}.`);
          }
        }, h("strong", {}, `Tier ${t}`), h("span", {}, rooms(roomLimit(t))))
      );
    }

    const count = h("p", { class: "rc-count", "data-testid": "rc-count" }, `${plan.rooms.length} of ${rooms(limit)} used`);
    const cap = full ? h("p", { class: "rc-cap", "data-testid": "rc-cap", role: "note" },
      plan.tier < 3 ? `Tier ${plan.tier} includes ${rooms(limit)}. Choose a higher tier to add more.` : `Tier 3 includes the maximum of ${rooms(limit)}.`) : null;
    const add = h("button", { type: "button", id: `${p}-add`, class: "btn btn-primary", "data-testid": "rc-add-room", disabled: full, "aria-describedby": full ? `${p}-capnote` : undefined,
      onclick: () => {
        if (!canAddRoom(plan)) return;
        const next = addRoom(plan);
        const r = next.rooms[next.rooms.length - 1]!;
        commit(next, true, `${p}-${r.id}-name`);
        say(`Room ${next.rooms.length} added. ${next.rooms.length} of ${rooms(limit)} used.`);
      } }, "Add room");
    if (cap) cap.id = `${p}-capnote`;

    const list = h("div", { class: "rc-rooms" });
    plan.rooms.forEach((r, i) => list.append(roomCard(r, i)));

    body.append(
      h("div", { class: "rc-top" },
        h("div", { class: "rc-field" }, h("label", { for: `${p}-retailer` }, "Retailer"), retailer),
        h("div", { class: "rc-field" }, h("span", { class: "rc-label", id: `${p}-tier-l` }, "Tier"), tiers)),
      h("div", { class: "rc-bar" }, count, add),
      cap ?? "",
      list
    );
    if (focusId) (body.querySelector<HTMLElement>(`#${CSS.escape(focusId)}`) ?? root).focus();
  }

  // Actions
  const issuesBox = h("div", { class: "rc-issues", "data-testid": "rc-issues", hidden: true, tabindex: -1 });
  const check = () => {
    const issues = validate(plan);
    clear(issuesBox);
    for (const el of body.querySelectorAll("[aria-invalid]")) el.removeAttribute("aria-invalid");
    if (issues.length === 0) {
      issuesBox.hidden = true;
      say(`Floor plan is complete: ${rooms(plan.rooms.length)} ready.`);
      return;
    }
    issuesBox.hidden = false;
    issuesBox.append(h("h3", {}, `${issues.length} thing${issues.length === 1 ? "" : "s"} to fix`), h("ul", {}, ...issues.map((x) => h("li", {}, x.message))));
    for (const x of issues) {
      const scope = x.where === "plan" ? body : body.querySelector(`[data-room="${CSS.escape(x.where)}"]`);
      scope?.querySelector(`[data-field="${x.field}"]`)?.setAttribute("aria-invalid", "true");
    }
    issuesBox.focus();
    say(`${issues.length} thing${issues.length === 1 ? "" : "s"} to fix.`);
  };
  const download = () => {
    const blob = new Blob([exportPlan(plan)], { type: "application/json" });
    const a = h("a", { href: URL.createObjectURL(blob), download: `floor-plan-${(plan.retailer.trim() || "retailer").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.json` });
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    say("Floor plan exported as JSON.");
  };
  const file = h("input", { type: "file", accept: "application/json,.json", class: "sr-only", id: `${p}-import`, "data-testid": "rc-import-file", tabindex: -1 });
  file.addEventListener("change", () => {
    const f = file.files?.[0];
    if (!f) return;
    void f.text().then((t) => {
      const next = parsePlan(t);
      file.value = "";
      if (!next) return say("That file is not a valid floor plan (check the tier and room limits).");
      commit(next);
      say(`Imported ${rooms(next.rooms.length)} for tier ${next.tier}.`);
    });
  });
  const reset = () => {
    if (typeof confirm === "function" && !confirm("Clear the whole floor plan?")) return;
    clearPlan(d.storage);
    plan = emptyPlan();
    savePlan(plan, d.storage);
    issuesBox.hidden = true;
    render(`${p}-retailer`);
    say("Floor plan reset.");
  };

  root.append(
    h("h2", { id: `${p}-title`, class: "rc-title" }, "Plan your floor"),
    h("p", { class: "muted rc-intro" }, "Add a room for each space on your floor: describe how it looks, who the assistant is and what guests can explore. Your plan is saved on this device."),
    body,
    issuesBox,
    h("div", { class: "rc-actions" },
      h("button", { type: "button", class: "btn btn-primary", "data-testid": "rc-check", onclick: check }, "Check plan"),
      h("button", { type: "button", class: "btn", "data-testid": "rc-export", onclick: download }, "Export JSON"),
      h("button", { type: "button", class: "btn", "data-testid": "rc-import", onclick: () => file.click() }, "Import JSON"),
      h("button", { type: "button", class: "btn", "data-testid": "rc-reset", onclick: reset }, "Reset"),
      d.close ? h("button", { type: "button", class: "btn", onclick: d.close }, "Done") : null,
      file),
    status
  );
  render();
  return root;
}
